import { useMemo } from 'react'

import { shallowEqual, useSelector } from 'react-redux'

import { EMPTY_LIST, EMPTY_OBJECT } from '@constants'
import { useCampaignContext } from '@context/Campaign'
import { IApplicationState } from '@store'
import { selectBudgetTotalCurrency } from '@store/campaigns'
import {
  selectIsFetchingSubmissionPerformance,
  selectSubmissionPerformance,
} from '@store/performance'
import { selectPostPurchaseBrandConnectMetrics } from '@store/postPurchaseAnalytics'
import {
  selectActiveSubmissionMediaObject,
  selectCPECurrency,
  selectSocialSubmissionType,
  selectSubmission,
  selectSubTotalCurrency,
} from '@store/submissions'
import { FINANCIAL_ANALYTICS_KEYS } from './mapping-functions'
import { AnalyticsData, MetricKey } from './types'

export const usePostPurchaseBreakdown = (submissionId: number) => {
  const { isUnlockedCampaign } = useCampaignContext()

  // Select only raw, reference-stable values (stored refs + primitives). The
  // presentation transform below builds fresh objects, which would loop under
  // react-redux 9 if returned straight from useSelector — so it runs in useMemo.
  const raw = useSelector((state: IApplicationState) => {
    const { identity_provider, identity_followers_count, brief_id, is_proxy, is_yt_brand_connect } =
      selectSubmission(state, submissionId)
    const cpeCurrency = selectCPECurrency(state, submissionId)
    const campaignBudgetCurrency = selectBudgetTotalCurrency(state, brief_id)
    const activeMediaObject = selectActiveSubmissionMediaObject(state, submissionId)

    return {
      isFetching: selectIsFetchingSubmissionPerformance(state),
      submissionPerformance: selectSubmissionPerformance(state, submissionId) || EMPTY_OBJECT,
      brandConnectMetrics: selectPostPurchaseBrandConnectMetrics(state, submissionId) || EMPTY_LIST,
      identityFollowersCount: identity_followers_count,
      isYouTubeBrandConnect: is_yt_brand_connect,
      currency:
        selectSubTotalCurrency(state, submissionId) ?? cpeCurrency ?? campaignBudgetCurrency,
      socialSubmissionType: selectSocialSubmissionType(state, submissionId),
      channel: identity_provider,
      mediaType: activeMediaObject?.media_type,
      isProxy: Boolean(is_proxy),
    }
  }, shallowEqual)

  return useMemo(() => {
    const {
      isFetching,
      submissionPerformance,
      brandConnectMetrics,
      identityFollowersCount,
      isYouTubeBrandConnect,
      currency,
      socialSubmissionType,
      channel,
      mediaType,
      isProxy,
    } = raw

    const analytics: AnalyticsData = {
      followers: identityFollowersCount,
      ...submissionPerformance,
    }

    const BRAND_CONNECT_HIDDEN_KEYS: MetricKey[] = [
      'impressions_pct',
      'reach_pct',
      'emv',
      'cpe',
      'cpv',
      'cpm',
      'emv_multiplier',
      'engagement',
    ]

    if (isYouTubeBrandConnect) {
      BRAND_CONNECT_HIDDEN_KEYS.forEach((key) => {
        analytics[key] = 'hidden'
      })
    }

    if (isUnlockedCampaign) {
      FINANCIAL_ANALYTICS_KEYS.forEach((key) => {
        analytics[key] = undefined
      })
    }

    return {
      isFetching,
      analytics,
      currency,
      socialSubmissionType,
      isProxy,
      channel,
      mediaType,
      isYouTubeBrandConnect,
      brandConnectMetrics,
    }
  }, [raw, isUnlockedCampaign])
}
